import { Injectable } from '@angular/core'
import hotkeys, { KeyHandler } from 'hotkeys-js'
import { HotkeyDestroyFn, HotkeyDetail, HotkeyMetadata } from './hotkey-options'

@Injectable({
  providedIn: 'root',
})
export class HotkeysService<T extends string> {
  /**
   * All the currently registered hotkeys.
   */
  hotkeys = new Map<T, HotkeyDetail>()
  /**
   * Set the current scope for the hotkey bindings.
   *
   * @see https://github.com/jaywcjlove/hotkeys#setscope
   */
  setScope = hotkeys.setScope
  /**
   * Removes all hotkeys associated with the scope.
   *
   * @see https://github.com/jaywcjlove/hotkeys#deletescope
   */
  deleteScope = hotkeys.deleteScope
  /**
   * Get the current scope
   *
   * @see https://github.com/jaywcjlove/hotkeys#getscope
   */
  getScope = hotkeys.getScope
  /**
   * @see https://github.com/jaywcjlove/hotkeys#ispressed
   */
  isPressed = hotkeys.isPressed
  /**
   * @see https://github.com/jaywcjlove/hotkeys#getpressedkeycodes
   */
  getPressedKeyCodes = hotkeys.getPressedKeyCodes

  /**
   * Removes all hotkeys
   *
   * @see https://github.com/jaywcjlove/hotkeys#unbind
   */
  clearAllHotkeys = () => {
    this.hotkeys = new Map()
    hotkeys.unbind()
  }

  /**
   * Set a function that filters when keypress will be evaluated against registered hotkeys
   *
   * @see https://github.com/jaywcjlove/hotkeys#filter
   */
  setEventFilter(fn: (e: Event) => boolean) {
    hotkeys.filter = fn
  }

  /**
   * Register multiple hotkeys. Hotkeys do not become active until each one calls `bind` with its type.
   */
  register(hotkeys: Map<T, HotkeyMetadata>): boolean[]
  /**
   * Register a hotkey. Hotkey does not become active until `bind` is called with they type.
   */
  register(type: T, hotkey: HotkeyMetadata): boolean
  register(
    type: T | Map<T, HotkeyMetadata>,
    hotkey?: HotkeyMetadata,
  ): boolean | boolean[] {
    if (type instanceof Map) {
      const registered = []
      for (const [t, h] of type) {
        registered.push(this.register(t, h))
      }
      return registered
    } else if (hotkey != null) {
      const detail = {
        ...hotkey,
        keyCombos: this.keyCombo(hotkey),
        bound: false,
      }

      const isRegistered = this.isRegisteredCombo(detail)
      if (isRegistered === false) {
        this.hotkeys.set(type, detail)
        return true
      } else {
        console.error('Duplicate hotkeys registered.', isRegistered)
        return false
      }
    } else {
      throw new Error('hotkey must be defined')
    }
  }

  unregister(type: T) {
    const hotkey = this.hotkeys.get(type)
    if (hotkey) {
      if (hotkey.bound) {
        this.unbind(hotkey)
      }
      this.hotkeys.delete(type)
    }
  }

  /**
   * Bind a handler to a registered hotkey type.
   *
   * @throws Hotkey type not found
   */
  bind(type: T, handler: KeyHandler): HotkeyDestroyFn
  /**
   * Create a new hotkey and bind it to a handler at the same time.
   *
   * @throws Duplicate key binding error
   */
  bind(type: T, hotkey: HotkeyMetadata, handler: KeyHandler): HotkeyDestroyFn
  bind(
    type: T,
    hotkeyOrHandler: HotkeyMetadata | KeyHandler,
    handler?: KeyHandler,
  ): HotkeyDestroyFn {
    if (typeof hotkeyOrHandler !== 'function') {
      const registered = this.register(type, hotkeyOrHandler)

      if (!registered) {
        throw new Error(
          `Duplicate hotkeys cannot be bound ${JSON.stringify(
            hotkeyOrHandler,
          )}`,
        )
      }
    }

    const h = this.hotkeys.get(type)

    if (h == null) {
      throw new Error(
        `${type} was not a registered hotkey. You must register a hotkey before you can bind a handler.`,
      )
    }

    // determine which parameter is the handler
    handler =
      handler != null
        ? handler
        : typeof hotkeyOrHandler === 'function'
        ? hotkeyOrHandler
        : () => {}

    const { key, options } = h
    hotkeys(key, options ?? {}, handler)
    h.bound = true
    h.handler = handler
    return () => {
      this.unbind(h)
      h.bound = false
    }
  }

  private unbind(hotkey: HotkeyDetail) {
    if (hotkey.options?.scope != null) {
      hotkeys.unbind(hotkey.key, hotkey.options.scope)
    } else {
      hotkeys.unbind(hotkey.key)
    }
  }

  private keyCombo(hotkey: HotkeyMetadata) {
    return hotkey.key.split(',').map(combo =>
      combo
        .trim()
        .split(hotkey.options?.splitKey ?? '+')
        .map(x => x.trim()),
    )
  }

  private isRegisteredCombo(hotkey: HotkeyDetail) {
    const duplicateCombos: string[] = []
    for (const hk of this.hotkeys.values()) {
      const hkScope = hk.options?.scope ?? ''
      for (const combo of hk.keyCombos) {
        const co = hkScope + combo.join('+').toLowerCase()
        const scope = hotkey.options?.scope ?? ''
        const hasCombo = hotkey.keyCombos.some(
          c => scope + c.join('+').toLowerCase() === co,
        )

        if (hasCombo) {
          duplicateCombos.push(co)
        }
      }
    }

    return duplicateCombos.length === 0 ? false : duplicateCombos
  }
}
